import { Spin } from 'antd'
import { Ruler, useRolodexRulers } from 'features/fde'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useGeckoLocale } from 'features/locale'
import _ from 'lodash'
import React, { FC, useMemo } from 'react'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { LABEL_TC, LABEL_TMM } from './FfsDataValuesSection'
import { LABEL_FCA_ML } from './FfsLevelOneUserInputSection'

export interface FfsFlawSizeCriteraSectionProps {
  loading: boolean
  componentModel: GeckoComponentModel | undefined
  flawSizeCheckResult: Omit<FlawSizeCriteriaCheckResult, 'isLoaing'>
}

export const LABEL_R_T = (
  <>
    R<sub>t</sub>
  </>
)

export const LABEL_TMM_MINUS_FCA = (
  <>
    t<sub>mm</sub> - FCA<sub>ml</sub>
  </>
)

export const LABEL_LMSD = (
  <>
    L<sub>msd</sub>
  </>
)

export const LABEL_LMSD_FLAW_SIZE_CRITERIA = (
  <>
    {LABEL_LMSD} ≥ 1.8 * sqrt(D t<sub>c</sub>)
  </>
)

interface FlawSizeCriteriaProps {
  componentType?: GeckoComponentModelType
  tmm: GeckoMeasurement | null
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  indicationId: string | undefined
  tc: GeckoMeasurement | null
  d: GeckoMeasurement | null
}

const WORKING_UNIT = GeckoMeasurementUnit.Millimeters
const withWorkingUnit: (val: number) => GeckoMeasurement = (val) =>
  GeckoMeasurement.CreateFromValue(val, WORKING_UNIT)

const LOSS_THRESHOLDS: Record<GeckoComponentModelType, GeckoMeasurement> = {
  [GeckoComponentModelType.VerticalTankShell]: withWorkingUnit(2.5),
  [GeckoComponentModelType.TankFloor]: withWorkingUnit(2.5),
  [GeckoComponentModelType.TankFlatRoof]: withWorkingUnit(2.5),
  [GeckoComponentModelType.HorizontalTankShell]: withWorkingUnit(2.5),
  [GeckoComponentModelType.PipeSection]: withWorkingUnit(1.3),
  [GeckoComponentModelType.LegacyBoilerWalls]: withWorkingUnit(2.5),
  [GeckoComponentModelType.ShipDeck]: withWorkingUnit(2.5),
  [GeckoComponentModelType.BasicShipHull]: withWorkingUnit(2.5),
  [GeckoComponentModelType.Cone]: withWorkingUnit(2.5),
  [GeckoComponentModelType.LegacyCone]: withWorkingUnit(2.5),
  [GeckoComponentModelType.LaunchTube]: withWorkingUnit(2.5),
  [GeckoComponentModelType.Dome]: withWorkingUnit(2.5),
  [GeckoComponentModelType.DomeWall]: withWorkingUnit(2.5),
  [GeckoComponentModelType.FlatCircularFloor]: withWorkingUnit(2.5),
  [GeckoComponentModelType.VerticalTankFoundation]: withWorkingUnit(2.5),
  [GeckoComponentModelType.MachinedForgingSimple]: withWorkingUnit(2.5),
  [GeckoComponentModelType.PressureVesselShell]: withWorkingUnit(2.5),
  [GeckoComponentModelType.HemisphericalHead]: withWorkingUnit(2.5),
  [GeckoComponentModelType.TorisphericalHead]: withWorkingUnit(2.5),
  [GeckoComponentModelType.EllipsoidalHead]: withWorkingUnit(2.5),
}

type FlawSizeCriteria = 'remainingThicknessRatio' | 'min_thickness' | 'lmsd'

export type FlawSizeCriteriaCheckResult = {
  isLoading: boolean
  criteriaValues: Record<FlawSizeCriteria, GeckoMeasurement | null>
  failingCriteria: ReadonlySet<FlawSizeCriteria>
  lmsdThreshold: GeckoMeasurement | null
  lossThreshold: GeckoMeasurement
}

function getRulerLength(ruler: Ruler): GeckoMeasurement {
  const xRange = ruler.selection.x.convertTo(WORKING_UNIT)
  const yRange = ruler.selection.y.convertTo(WORKING_UNIT)
  const xLen = Math.abs(xRange.max.num - xRange.min.num)
  const yLen = Math.abs(yRange.max.num - yRange.min.num)
  return GeckoMeasurement.CreateFromValue(
    Math.sqrt(Math.pow(xLen, 2) + Math.pow(yLen, 2)),
    WORKING_UNIT
  )
}

const REMAINING_THICKNESS_THRESHOLD_RATIO = 0.2

function getLmsdThreshold(
  d: GeckoMeasurement | null,
  tc: GeckoMeasurement | null
): GeckoMeasurement | null {
  return _.isNil(d) || _.isNil(tc)
    ? null
    : GeckoMeasurement.CreateFromValue(
        1.8 *
          Math.sqrt(
            d.convertTo(WORKING_UNIT).num * tc.convertTo(WORKING_UNIT).num
          ),
        WORKING_UNIT
      )
}

export function useLevelOneFlawSizeCriteriaCheck({
  componentType,
  tmm,
  fcaMl,
  fca,
  tc,
  d,
  indicationId,
}: FlawSizeCriteriaProps): FlawSizeCriteriaCheckResult {
  const { rulers, isLoading: rulersLoading } = useRolodexRulers(
    useMemo(
      () => (indicationId ? { indication: indicationId } : undefined),
      [indicationId]
    )
  )

  const rt = useMemo(
    () =>
      _.isNil(tmm) || _.isNil(fcaMl) || _.isNil(tc) || _.isNil(fca)
        ? null
        : GeckoMeasurement.CreateFromValue(
            (tmm.convertTo(WORKING_UNIT_THICKNESS).num -
              fcaMl.convertTo(WORKING_UNIT_THICKNESS).num) /
              tc.convertTo(WORKING_UNIT_THICKNESS).num,
            GeckoMeasurementUnit.Ratio
          ),
    [tc, fca, fcaMl, tmm]
  )

  return useMemo(() => {
    const lossThreshold = getFcaLimitThreshold(componentType)
    const lmsdThreshold = getLmsdThreshold(d, tc)
    const criteriaValues = {
      remainingThicknessRatio: rt,
      min_thickness:
        _.isNil(tmm) || _.isNil(fcaMl)
          ? null
          : withWorkingUnit(
              tmm.convertTo(WORKING_UNIT).num -
                fcaMl?.convertTo(WORKING_UNIT).num
            ),
      lmsd: _.minBy(rulers?.map(getRulerLength), (meas) => meas.num) ?? null,
    }

    const failingCriteria = new Set<FlawSizeCriteria>()

    if (
      _.isNil(criteriaValues.remainingThicknessRatio) ||
      criteriaValues.remainingThicknessRatio.num <
        REMAINING_THICKNESS_THRESHOLD_RATIO
    ) {
      failingCriteria.add('remainingThicknessRatio')
    }
    if (
      _.isNil(criteriaValues.min_thickness) ||
      _.isNil(lossThreshold) ||
      criteriaValues.min_thickness.convertTo(GeckoMeasurementUnit.Millimeters)
        .num < lossThreshold?.num
    ) {
      failingCriteria.add('min_thickness')
    }
    if (
      _.isNil(criteriaValues.lmsd) ||
      _.isNil(lmsdThreshold) ||
      criteriaValues.lmsd.num < lmsdThreshold.num
    ) {
      failingCriteria.add('lmsd')
    }

    return {
      isLoading: rulersLoading,
      criteriaValues,
      failingCriteria,
      lmsdThreshold,
      lossThreshold,
    }
  }, [componentType, d, fcaMl, rt, rulers, rulersLoading, tc, tmm])
}

function getFcaLimitThreshold(
  componentType: GeckoComponentModelType | undefined
): GeckoMeasurement {
  return componentType
    ? LOSS_THRESHOLDS[componentType]
    : GeckoMeasurement.CreateFromValue(2.5, GeckoMeasurementUnit.Millimeters)
}

const TooltipContentTmmFcaLimit: FC<{
  componentType?: GeckoComponentModelType
}> = ({ componentType }) => {
  const threshold = useMemo<GeckoMeasurement>(
    () => getFcaLimitThreshold(componentType),
    [componentType]
  )
  const { formatMeasurement } = useGeckoLocale()
  return (
    <>
      {LABEL_TMM} - {LABEL_FCA_ML} {'>'}{' '}
      <span>{formatMeasurement({ measurement: threshold })}</span>
    </>
  )
}

const RawFfsFlawSizeCriteriaSection: FC<FfsFlawSizeCriteraSectionProps> = ({
  loading,
  componentModel,
  flawSizeCheckResult,
}) => {
  const {
    criteriaValues: flawSizeCriteriaCheckValues,
    failingCriteria: flawSizeFailingCritera,
    lossThreshold,
    lmsdThreshold,
  } = flawSizeCheckResult
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_R_T}
          labelTooltip={
            <>
              ({LABEL_TMM} - {LABEL_FCA_ML}) / {LABEL_TC}
            </>
          }
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.remainingThicknessRatio}
          measurementType='ratio'
          isValid={!flawSizeFailingCritera.has('remainingThicknessRatio')}
          trailingText={`> ${REMAINING_THICKNESS_THRESHOLD_RATIO}`}
        />
        <FfsParameterField
          label={LABEL_TMM_MINUS_FCA}
          labelTooltip={
            <TooltipContentTmmFcaLimit componentType={componentModel?.type} />
          }
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.min_thickness}
          isValid={!flawSizeFailingCritera.has('min_thickness')}
          trailingText={(value) => {
            const thresholdInValueUnit = lossThreshold?.convertTo(
              value?.unit ?? lossThreshold.unit
            )
            return `> ${thresholdInValueUnit.num}`
          }}
        />
        <FfsParameterField
          label={LABEL_LMSD}
          labelTooltip={LABEL_LMSD_FLAW_SIZE_CRITERIA}
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.lmsd}
          isValid={!flawSizeFailingCritera.has('lmsd')}
          trailingText={(value) => {
            const unit = value?.unit
            const thresholdInValueUnit = lmsdThreshold?.convertTo(
              unit ?? WORKING_UNIT
            )
            return `> ${thresholdInValueUnit?.num.toFixed(2)}`
          }}
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsFlawSizeCriteriaSection = React.memo(
  RawFfsFlawSizeCriteriaSection
)
